import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { isEqual } from "lodash";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { EnergyManagementImport } from "../../../types/imports";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ChartDataLabels,
  Tooltip,
);

const SEGMENT_COLORS = [
  "#E6194B",
  "#3CB44B",
  "#FFE119",
  "#4363D8",
  "#F58231",
  "#BB66FF",
  "#46F0F0",
  "#F032E6",
  "#BCF60C",
  "#FABEBE",
  "#008080",
  "#E6BEFF",
  "#9A6324",
  "#FFFAC8",
  "#800000",
  "#AAFFC3",
  "#808000",
  "#FFD8B1",
  "#000075",
  "#808080",
  "#FF6F69",
  "#6B5B95",
  "#88B04B",
  "#F7CAC9",
  "#92A8D1",
  "#955251",
  "#B565A7",
  "#009B77",
  "#DD4124",
  "#45B8AC",
];

function EMSegmentGraph() {
  const emImport: EnergyManagementImport | null = useSelector(
    (state: RootState) => state.imports?.importPreviews?.em,
    isEqual,
  );

  const { chartData, bounds } = useMemo(() => {
    if (
      !emImport ||
      !emImport.data ||
      !emImport.data.TrackMapX ||
      !emImport.data.TrackMapYSegments
    ) {
      return {
        chartData: { labels: [], datasets: [] },
        bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
      };
    }

    const datasets = [];
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    const segmentDataSets: Record<number, { x: number; y: number }[]> = {};

    for (const [
      coordinateIndex,
      yPoint,
    ] of emImport.data.TrackMapYSegments.entries()) {
      for (const [segmentIndex, yCoordinate] of yPoint.entries()) {
        if (yCoordinate === null) continue;
        if (!segmentDataSets[segmentIndex + 1]) {
          segmentDataSets[segmentIndex + 1] = [];
        }
        segmentDataSets[segmentIndex + 1].push({
          x: emImport.data.TrackMapX[coordinateIndex],
          y: yCoordinate,
        });
      }
    }

    for (const segmentIndex in segmentDataSets) {
      let coordinateData = segmentDataSets[segmentIndex];

      // Track bounds
      const xValues = coordinateData.map((pt) => pt.x);
      const yValues = coordinateData.map((pt) => pt.y);
      minX = Math.min(minX, ...xValues);
      maxX = Math.max(maxX, ...xValues);
      minY = Math.min(minY, ...yValues);
      maxY = Math.max(maxY, ...yValues);

      // For segment 1, start after the large jump (finish line).
      // Otherwise the segment will loop back on itself
      if (segmentIndex === "1") {
        const jumpThreshold = 100;
        let jumpIndex = -1;
        for (let j = 1; j < coordinateData.length; j++) {
          const dx = Math.abs(coordinateData[j].x - coordinateData[j - 1].x);
          const dy = Math.abs(coordinateData[j].y - coordinateData[j - 1].y);
          if (dx > jumpThreshold || dy > jumpThreshold) {
            jumpIndex = j;
            break;
          }
        }
        if (jumpIndex > 0) {
          coordinateData = [
            ...coordinateData.slice(jumpIndex),
            ...coordinateData.slice(0, jumpIndex),
          ];
        }
      }

      const midIndex = Math.floor(coordinateData.length / 2);

      datasets.push({
        label: `Segment ${segmentIndex}`,
        data: coordinateData,
        pointRadius: 0,
        borderColor: SEGMENT_COLORS[parseInt(segmentIndex) - 1],
        backgroundColor: SEGMENT_COLORS[parseInt(segmentIndex) - 1],
        borderWidth: 2,
        showLine: true,
        datalabels: {
          display: (context: Context) => context.dataIndex === midIndex,
          formatter: () => `${segmentIndex}`,
          color: SEGMENT_COLORS[parseInt(segmentIndex) - 1],
          textStrokeColor: "#2c2c2c",
          textStrokeWidth: 10,
          font: {
            weight: "bold" as const,
            size: 16,
          },
          anchor: "center" as const,
          align: "top" as const,
        },
      });
    }

    // Add 5% padding to bounds
    const paddingX = (maxX - minX) * 0.05;
    const paddingY = (maxY - minY) * 0.05;

    return {
      chartData: { labels: [], datasets },
      bounds: {
        minX: minX - paddingX,
        maxX: maxX + paddingX,
        minY: minY - paddingY,
        maxY: maxY + paddingY,
      },
    };
  }, [emImport]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {},
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        color: "#ffffff",
        text: "Segments Track Map",
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<"line">) => context.dataset.label,
          title: () => "",
        },
        displayColors: false,
      },
    },
    scales: {
      x: {
        display: false,
        type: "linear" as const,
        min: bounds.minX,
        max: bounds.maxX,
      },
      y: {
        display: false,
        min: bounds.minY,
        max: bounds.maxY,
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default EMSegmentGraph;
